<script lang="ts">
	import '../app.css';
	import { onMount } from 'svelte';
	import {appEntrypoint, userContainer} from "$lib/stores/stores.svelte"
	import {fade} from 'svelte/transition';
	import { page } from '$app/state';
	import { page as pageStore } from '$app/stores';
	import { locales, localizeHref, baseLocale, getLocale } from '$lib/paraglide/runtime';
	import { textDirection } from '$lib/utils/textDirection';
	import { afterNavigate, beforeNavigate, onNavigate, preloadData } from '$app/navigation';
	import FiltersForMobile from '$lib/components/FiltersForMobile.svelte';
	import { setViewTransitioning } from '$lib/stores/viewTransition.svelte';

	// Version identifier - changes on each build/dev restart
	const BUILD_ID = 'A6rVt3Km';

	// The SPA fallback shell (admin.html) is rendered once in the base locale,
	// so set lang/dir from the URL once the client knows the real locale.
	$effect(() => {
		page.url.pathname;
		const locale = getLocale();
		document.documentElement.lang = locale;
		document.documentElement.dir = textDirection(locale);
	});

	// note the current page before navigating
	beforeNavigate((navigation) => {
		userContainer.prevPage = page.url.pathname;
	});

	// Clear search text after navigation completes (allows view transition to capture search results first)
	// Also preload parent list page for fast back navigation from detail pages
	afterNavigate(({ to }) => {
		if (userContainer.searchText.length > 0) {
			userContainer.searchText = '';
		}

		// Preload list page when on a detail page (for instant back navigation)
		const path = to?.url.pathname ?? '';
		if (path.match(/^\/[a-z]{2}\/shoes\/[^/]+\/?$/) || path.match(/^\/shoes\/[^/]+\/?$/)) {
			// On shoe detail page - preload shoes list
			preloadData(localizeHref('/shoes/'));
		} else if (path.match(/^\/[a-z]{2}\/mats\/[^/]+\/?$/) || path.match(/^\/mats\/[^/]+\/?$/)) {
			// On mats detail page - preload mats list
			preloadData(localizeHref('/mats/'));
		}
	});

	onNavigate((navigation) => {
		// Skip if View Transitions API not supported
		if (!document.startViewTransition) return;

		setViewTransitioning(true);
		return new Promise((resolve) => {
			const transition = document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
			transition.finished.then(() => setViewTransitioning(false));
		});
	});	

    // --- Props ---
	let {
		children
	} = $props();


  // --- State ---

	let wait = $state(true)

	// Check if we're on QA page (skip header/layout for QA)
	// Using $pageStore for reliable reactivity during SSR and hydration
	let isQAPage = $derived($pageStore.url.pathname.startsWith('/qa'));

	$effect(()=>{
		$inspect(userContainer.showingMobileFilters).with(console.trace);
	})

onMount(async ()=>{
	console.log(`[BUILD] ${BUILD_ID}`);
	try {
		await appEntrypoint()
	} catch (error) {
		console.log("error on app entrypoint! "+error)
	} finally {
		wait = false;
	}
})

// Dark mode handling - respects system preference, with QA override
$effect(() => {
	const qaTheme = page.url.searchParams.get('_qa_theme');

	if (qaTheme === 'dark') {
		// QA override: force dark
		document.documentElement.classList.add('dark');
	} else if (qaTheme === 'light') {
		// QA override: force light
		document.documentElement.classList.remove('dark');
	} else {
		// No override: respect system preference
		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
		document.documentElement.classList.toggle('dark', prefersDark);
	}
});

// Listen for system dark mode changes
onMount(() => {
	const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
	const handleChange = (e: MediaQueryListEvent) => {
		// Only apply if no QA override is active
		if (!page.url.searchParams.has('_qa_theme')) {
			document.documentElement.classList.toggle('dark', e.matches);
		}
	};
	mediaQuery.addEventListener('change', handleChange);
	return () => mediaQuery.removeEventListener('change', handleChange);
});

$effect(() => {
	console.log("PREV PAGE ON LAYOUT: "+userContainer.prevPage)
	console.log("SEARCH TEXT LENGTH: "+userContainer.searchText.length)
	console.log("SHOWING SEARCH: "+userContainer.showingSearch)
})
import Header from '$lib/components/Header.svelte';
	import Footer from '$lib/components/Footer.svelte';
	import Backdrop from './backdrop.svelte';

	const SITE = 'https://healthyshoes.com.cy';

	function getBasePath(path: string): string {
		return path.replace(/^\/(el|ru|he)\//, '/');
	}

	function buildLocalizedUrl(basePath: string, locale: string): string {
		if (locale === baseLocale) return SITE + basePath;
		return SITE + '/' + locale + basePath;
	}

	const canonicalUrl = $derived(SITE + page.url.pathname);
	const basePath = $derived(getBasePath(page.url.pathname));
</script>

<svelte:head>
	<link rel="canonical" href={canonicalUrl} />
	{#each locales as locale}
		<link rel="alternate" hreflang={locale} href={buildLocalizedUrl(basePath, locale)} />
	{/each}
	<link rel="alternate" hreflang="x-default" href={buildLocalizedUrl(basePath, baseLocale)} />
	<meta property="og:url" content={canonicalUrl} />
	<meta property="og:site_name" content="Healthy Shoes Cyprus" />
</svelte:head>
{#if isQAPage}
	<!-- QA page renders without layout chrome -->
	{@render children()}
{:else}
<div class="">
	{#if userContainer.showingMobileFilters}
	showing just filters
	<FiltersForMobile></FiltersForMobile>
{:else if userContainer.showingSearch && userContainer.searchText.length > 0}
<div
transition:fade={{ duration: 200 }}
>
<Backdrop></Backdrop>
</div>
{:else}
<!-- Header - uses view-transition-name to stay in place during navigation -->
<header class="sticky top-0 z-20" style="view-transition-name: site-header;">
	<Header></Header>
</header>
<!-- Main Content Slot -->
		<!-- flex-grow ensures it tries to fill space. Add min-h-0 for flex sizing safety -->
		<!-- overflow-y-auto allows main content to scroll *when body scroll isn't locked* -->
		<!-- <ScrollTracker>
			{#snippet children(progress: number)} -->

		<main

		>
			{@render children()}
		</main>
		<Footer />
		<!-- {/snippet}
		  </ScrollTracker> -->
{/if}
</div>
<div style="display:none">
	{#each locales as locale}
		<a href={localizeHref(page.url.pathname, { locale })}>{locale}</a>
	{/each}
</div>
{/if}

